import React from "react";
import { useI18n } from "../i18n";
import { daysAgoIso, monthStartIso, todayIso } from "../utils/dates";

/**
 * A from–to day filter with the ranges an owner actually asks about one tap
 * away. Days are local "YYYY-MM-DD" strings; the API treats `end_date` as the
 * whole of that day.
 */
const PRESETS = {
  today: () => ({ start_date: todayIso(), end_date: todayIso() }),
  yesterday: () => ({ start_date: daysAgoIso(1), end_date: daysAgoIso(1) }),
  last7: () => ({ start_date: daysAgoIso(6), end_date: todayIso() }),
  thisMonth: () => ({ start_date: monthStartIso(), end_date: todayIso() }),
  all: () => ({ start_date: undefined, end_date: undefined }),
};

export const rangeFor = (preset) => ({ preset, ...PRESETS[preset]() });

const chipClass = (active) =>
  `shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
    active
      ? "bg-primary-600 border-primary-600 text-white"
      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
  }`;

/** value: { preset, start_date, end_date } — see rangeFor. */
const DateRangeFilter = ({ value, onChange }) => {
  const { t } = useI18n();
  const custom = value.preset === "custom";

  const setDay = (field, day) => {
    const next = { ...value, preset: "custom", [field]: day || undefined };

    // Picking a "from" after the "to" (or the other way round) means the
    // owner is moving the whole window, not asking for an empty one
    if (next.start_date && next.end_date && next.start_date > next.end_date) {
      if (field === "start_date") next.end_date = day;
      else next.start_date = day;
    }

    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label={t("common.dateRange.label")}>
        {Object.keys(PRESETS).map((preset) => (
          <button
            key={preset}
            type="button"
            aria-pressed={value.preset === preset}
            onClick={() => onChange(rangeFor(preset))}
            className={chipClass(value.preset === preset)}
          >
            {t(`common.dateRange.${preset}`)}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={custom}
          onClick={() => onChange({ ...value, preset: "custom" })}
          className={chipClass(custom)}
        >
          {t("common.dateRange.custom")}
        </button>
      </div>

      {custom && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-600">
            <span className="block mb-1">{t("common.dateRange.from")}</span>
            <input
              type="date"
              dir="ltr"
              value={value.start_date || ""}
              max={todayIso()}
              onChange={(e) => setDay("start_date", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            />
          </label>
          <label className="text-sm text-gray-600">
            <span className="block mb-1">{t("common.dateRange.to")}</span>
            <input
              type="date"
              dir="ltr"
              value={value.end_date || ""}
              max={todayIso()}
              onChange={(e) => setDay("end_date", e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
